import React, { memo } from 'react'
import { CheckIcon } from '@heroicons/react/20/solid'
import { t } from 'i18next'

export const LuggageDetailsForm = memo(function LuggageDetailsForm({
    luggage,
    index,
    luggageDetails,
    luggageTags,
    updateLuggageLoading,
    printLuggageLoading,
    onLuggageDetailsChange,
    onUpdateLuggage,
    onPrintLuggage,
    onAddLuggage,
    onRemoveLuggage,
    addLuggageLoading,
    removeLuggageLoading,
    isCheckedIn: parentIsCheckedIn,
    savedLuggageDetails = {}
}) {
    const details = luggageDetails[luggage.luggage_id] || {}

    // Validation: weight is mandatory and check-in status
    const isWeightValid = details.weight && parseFloat(details.weight) > 0
    // Use the parent's check-in status
    const isCheckedIn = parentIsCheckedIn

    // Check if current details match the saved state (no modifications since last save)
    const savedState = savedLuggageDetails[luggage.luggage_id]
    const currentState = JSON.stringify(details)
    const hasBeenSaved = savedState !== undefined
    const hasModifications = hasBeenSaved && savedState !== currentState

    // Disable update button if: no valid weight, loading, checked in, OR saved without modifications
    const isUpdateDisabled = !isWeightValid || updateLuggageLoading || isCheckedIn || (hasBeenSaved && !hasModifications)

    return (
        <div className="bg-white rounded-xl p-5 shadow-sm hover:shadow-md transition-shadow duration-200">
            {/* Header */}
            <div className="flex justify-between items-center mb-6 pb-4 border-b border-gray-100">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                        <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                        </svg>
                    </div>
                    <div>
                        <h6 className="text-sm font-semibold text-gray-900">
                            {t('Luggage')} #{index + 1}
                        </h6>
                        <p className="text-xs text-gray-500 font-mono">{luggage.luggage_id}</p>
                    </div>
                </div>
                {luggage.status && (
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${luggage.status.code === 'ACC' ? 'bg-green-50 text-green-700 ring-1 ring-green-600/20' :
                            luggage.status.code === 'REJ' ? 'bg-red-50 text-red-700 ring-1 ring-red-600/20' :
                                luggage.status.code === 'PEN' ? 'bg-yellow-50 text-yellow-700 ring-1 ring-yellow-600/20' :
                                    'bg-gray-50 text-gray-700 ring-1 ring-gray-600/20'
                        }`}>
                        {luggage.status.description?.en || luggage.status.code}
                    </span>
                )}
            </div>

            {/* Dimensions Grid */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
                <div>
                    <label className="block text-xs font-medium text-gray-600 mb-2">
                        {t('Weight (kg)')} <span className="text-red">*</span>
                    </label>
                    <input
                        type="number"
                        step="0.1"
                        min="0.1"
                        value={details.weight || ''}
                        onChange={(e) => onLuggageDetailsChange(luggage.luggage_id, 'weight', e.target.value)}
                        disabled={isCheckedIn}
                        className={`w-full px-3 py-2.5 border-0 rounded-lg focus:outline-none focus:ring-2 focus:bg-white transition-all text-sm ${details.weight && parseFloat(details.weight) <= 0
                                ? 'bg-red-50 focus:ring-red-500/50'
                                : isCheckedIn ? 'bg-gray-100 text-gray-500' : 'bg-gray-50 focus:ring-primary/50'
                            }`}
                        placeholder="0.0"
                        required
                    />
                    {details.weight && parseFloat(details.weight) <= 0 && (
                        <p className="text-xs text-red-500 mt-1">{t('Weight must be greater than 0')}</p>
                    )}
                </div>

                <div>
                    <label className="block text-xs font-medium text-gray-600 mb-2">
                        {t('Length (cm)')}
                    </label>
                    <input
                        type="number"
                        step="0.1"
                        value={details.length || '70'}
                        onChange={(e) => onLuggageDetailsChange(luggage.luggage_id, 'length', e.target.value)}
                        disabled={isCheckedIn}
                        className={`w-full px-3 py-2.5 border-0 rounded-lg focus:outline-none focus:ring-2 focus:bg-white transition-all text-sm ${isCheckedIn ? 'bg-gray-100 text-gray-500' : 'bg-gray-50 focus:ring-primary/50'
                            }`}
                        placeholder="0.0"
                    />
                </div>

                <div>
                    <label className="block text-xs font-medium text-gray-600 mb-2">
                        {t('Width (cm)')}
                    </label>
                    <input
                        type="number"
                        step="0.1"
                        value={details.width || '50'}
                        onChange={(e) => onLuggageDetailsChange(luggage.luggage_id, 'width', e.target.value)}
                        disabled={isCheckedIn}
                        className={`w-full px-3 py-2.5 border-0 rounded-lg focus:outline-none focus:ring-2 focus:bg-white transition-all text-sm ${isCheckedIn ? 'bg-gray-100 text-gray-500' : 'bg-gray-50 focus:ring-primary/50'
                            }`}
                        placeholder="0.0"
                    />
                </div>

                <div>
                    <label className="block text-xs font-medium text-gray-600 mb-2">
                        {t('Height (cm)')}
                    </label>
                    <input
                        type="number"
                        step="0.1"
                        value={details.height || '38'}
                        onChange={(e) => onLuggageDetailsChange(luggage.luggage_id, 'height', e.target.value)}
                        disabled={isCheckedIn}
                        className={`w-full px-3 py-2.5 border-0 rounded-lg focus:outline-none focus:ring-2 focus:bg-white transition-all text-sm ${isCheckedIn ? 'bg-gray-100 text-gray-500' : 'bg-gray-50 focus:ring-primary/50'
                            }`}
                        placeholder="0.0"
                    />
                </div>
            </div>

            {/* Tags */}
            <div className="mb-6">
                <label className="block text-xs font-medium text-gray-600 mb-3">
                    {t('Tags')}
                </label>
                <div className="flex flex-wrap gap-2">
                    {luggageTags.map(tag => (
                        <label key={tag.code} className={`flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer transition-all ${(details.tags || []).includes(tag.code)
                                ? 'bg-primary/10 text-primary ring-2 ring-primary/30'
                                : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
                            }`}>
                            <input
                                type="checkbox"
                                checked={(details.tags || []).includes(tag.code)}
                                onChange={(e) => {
                                    const currentTags = details.tags || []
                                    const newTags = e.target.checked
                                        ? [...currentTags, tag.code]
                                        : currentTags.filter(t => t !== tag.code)
                                    onLuggageDetailsChange(luggage.luggage_id, 'tags', newTags)
                                }}
                                disabled={isCheckedIn}
                                className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded disabled:opacity-50"
                            />
                            <span className="text-sm font-medium">{tag.description?.en || tag.code}</span>
                        </label>
                    ))}
                </div>
            </div>

            {/* Actions */}
            <div className="flex justify-between gap-3 pt-4 border-t border-gray-100">
                <div className="flex gap-2">
                    {/* Remove Luggage Button - Show for both Created and Accepted status, but hide if checked in */}
                    {(luggage.status?.code === 'CRT' || luggage.status?.code === 'ACC') && !isCheckedIn && (
                        <button
                            onClick={() => onRemoveLuggage && onRemoveLuggage(luggage)}
                            disabled={removeLuggageLoading || isCheckedIn}
                            className="relative inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold text-gray-700 bg-white border border-gray-300 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {removeLuggageLoading ? (
                                <div className="w-4 h-4 border-2 border-gray-700 border-t-transparent rounded-full animate-spin" />
                            ) : (
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                            )}
                            {t('Remove Luggage')}
                        </button>
                    )}
                </div>

                <div className="flex gap-2">
                    {/* Print Tag Button */}
                    <button
                        onClick={() => onPrintLuggage && onPrintLuggage(luggage)}
                        disabled={printLuggageLoading}
                        className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-gray-700 bg-gray-50 rounded-lg hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {printLuggageLoading ? (
                            <>
                                <div className="w-4 h-4 border-2 border-gray-700 border-t-transparent rounded-full animate-spin" />
                                {t('Printing...')}
                            </>
                        ) : (
                            <>
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                                </svg>
                                {t('Print Tag')}
                            </>
                        )}
                    </button>

                    {/* Update Luggage Button - Hide if checked in */}
                    {!isCheckedIn && (
                        <button
                            onClick={() => onUpdateLuggage(luggage)}
                            disabled={!isWeightValid || updateLuggageLoading}
                            className={`flex items-center gap-2 px-5 py-2.5 text-sm font-medium rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 transition-all shadow-sm hover:shadow ${!isWeightValid || updateLuggageLoading
                                    ? 'text-gray-400 bg-gray-200 cursor-not-allowed'
                                    : 'text-white bg-primary hover:bg-primary/90 focus:ring-primary'
                                }`}
                        >
                            {updateLuggageLoading ? (
                                <>
                                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                    {t('Updating...')}
                                </>
                            ) : (
                                <>
                                    <CheckIcon className="w-4 h-4" />
                                    {t('Update Luggage')}
                                </>
                            )}
                        </button>
                    )}
                </div>
            </div>
        </div>
    )
})



